import { z } from 'zod';
import { isoDaySchema as isoDate } from './dates';

/** Diretriz estratégica do projeto (orientação geral que guia os objetivos), em ordem de prioridade. */
export const guidelineSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  position: z.number().int(),
  archivedAt: z.string().nullable(),
});
export type Guideline = z.infer<typeof guidelineSchema>;
export const guidelineListSchema = z.object({ items: z.array(guidelineSchema) });

export const guidelineInputSchema = z.object({
  title: z.string().trim().min(3, 'Escreva a diretriz.').max(200, 'Diretriz longa demais.'),
  description: z
    .string()
    .trim()
    .max(1000, 'A explicação pode ter no máximo 1000 caracteres.')
    .nullable()
    .optional()
    .transform((v) => v || null),
});
export type GuidelineInput = z.input<typeof guidelineInputSchema>;

/** Máximo de diretrizes ativas por projeto (criar e restaurar respeitam; a ordem aceita todas). */
export const MAX_ACTIVE_GUIDELINES = 100;

/** Nova ordem das diretrizes ativas do projeto (todas, da primeira à última). */
export const guidelineOrderSchema = z.object({
  ids: z
    .array(z.string().uuid())
    .min(1)
    .max(MAX_ACTIVE_GUIDELINES)
    .refine((ids) => new Set(ids).size === ids.length, 'Diretriz repetida na ordem.'),
});

export const PREMISE_CATEGORIES = [
  'ECONOMIC',
  'MARKET',
  'OPERATIONAL',
  'FINANCIAL',
  'PEOPLE',
  'REGULATORY',
  'TECHNOLOGY',
  'OTHER',
] as const;
export type PremiseCategoryName = (typeof PREMISE_CATEGORIES)[number];
export const PREMISE_CATEGORY_LABELS: Record<PremiseCategoryName, string> = {
  ECONOMIC: 'Economia',
  MARKET: 'Mercado',
  OPERATIONAL: 'Operação',
  FINANCIAL: 'Financeira',
  PEOPLE: 'Pessoas',
  REGULATORY: 'Regulatória',
  TECHNOLOGY: 'Tecnologia',
  OTHER: 'Outra',
};

/** Premissa do planejamento: o que se assume como verdade, com categoria e vigência. */
export const premiseSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  category: z.enum(PREMISE_CATEGORIES),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  archivedAt: z.string().nullable(),
});
export type Premise = z.infer<typeof premiseSchema>;
export const premiseListSchema = z.object({ items: z.array(premiseSchema) });

export const premiseInputSchema = z
  .object({
    text: z.string().trim().min(3, 'Escreva a premissa.').max(500, 'Premissa longa demais.'),
    category: z.enum(PREMISE_CATEGORIES, { message: 'Escolha a categoria.' }),
    validFrom: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    validTo: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
  })
  .refine((v) => !v.validFrom || !v.validTo || v.validFrom <= v.validTo, {
    message: 'O fim da vigência precisa ser igual ou posterior ao início.',
    path: ['validTo'],
  });
export type PremiseInput = z.input<typeof premiseInputSchema>;

/** Premissa vigente numa data (sem início = desde sempre; sem fim = sem prazo). */
export function premiseInForce(p: Pick<Premise, 'validFrom' | 'validTo'>, day: string): boolean {
  return (!p.validFrom || p.validFrom <= day) && (!p.validTo || p.validTo >= day);
}
